import { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Upload, ClipboardList, Truck, Package, BarChart3,
  FileCheck, LogOut, ChevronRight, Users, ShieldCheck,
  Settings, UserPlus, X, Bell, Archive, Calculator, Briefcase
} from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";

const NAV_ITEMS = {
  SS: [
    { path: "/upload", label: "Upload Jobs", icon: Upload },
    { path: "/jobs", label: "My Jobs", icon: ClipboardList },
    { path: "/archive", label: "Archive", icon: Archive },
  ],
  SM: [
    { path: "/dashboard", label: "Dashboard", icon: BarChart3 },
    { path: "/jobs", label: "All Jobs", icon: ClipboardList },
    { path: "/approvals", label: "Approvals", icon: ShieldCheck },
    { path: "/notifications", label: "Notifications", icon: Bell },
    { path: "/archive", label: "Archive", icon: Archive },
  ],
  CI: [
    { path: "/capture", label: "Capture Jobs", icon: FileCheck },
    { path: "/jobs", label: "All Jobs", icon: ClipboardList },
    { path: "/notifications", label: "Notifications", icon: Bell },
    { path: "/approvals", label: "Approvals", icon: ShieldCheck },
    { path: "/archive", label: "Archive", icon: Archive },
  ],
  CS: [
    { path: "/local-deliveries", label: "Local Deliveries Board", icon: Truck },
    { path: "/staff", label: "Depot & Delivery", icon: Package },
    { path: "/jobs", label: "All Jobs", icon: ClipboardList },
    { path: "/notifications", label: "Notifications", icon: Bell },
    { path: "/approvals", label: "Approvals", icon: ShieldCheck },
    { path: "/archive", label: "Archive", icon: Archive },
  ],
  CM: [
    { path: "/dashboard", label: "Dashboard", icon: BarChart3 },
    { path: "/capture", label: "Capture Jobs", icon: FileCheck },
    { path: "/jobs", label: "All Jobs", icon: ClipboardList },
    { path: "/notifications", label: "Notifications", icon: Bell },
    { path: "/approvals", label: "Approvals", icon: ShieldCheck },
    { path: "/accounts", label: "Accounts", icon: Briefcase },
    { path: "/accounts/archive", label: "Accounts Archive", icon: Archive },
    { path: "/staff", label: "Depot & Delivery", icon: Package },
    { path: "/archive", label: "Archive", icon: Archive },
    { path: "/users", label: "User Management", icon: UserPlus },
    { path: "/external-users", label: "External Users", icon: UserPlus },
    { path: "/config", label: "System Config", icon: Settings },
  ],
  "3PA": [
    { path: "/agent", label: "Assigned Jobs", icon: Truck },
    { path: "/jobs", label: "All Jobs", icon: ClipboardList },
    { path: "/notifications", label: "Notifications", icon: Bell },
  ],
  EXTERNAL: [
    { path: "/jobs", label: "My Jobs", icon: ClipboardList },
    { path: "/notifications", label: "Notifications", icon: Bell },
  ],
  SA: [
    { path: "/accounting", label: "Completed JC", icon: Calculator },
    { path: "/accounting/archive", label: "Archive", icon: Archive },
  ],
};

const ROLE_LABELS = {
  SS: "Skyworth Staff",
  SM: "Skyworth Management",
  CI: "CargoDash Inhouse",
  CS: "CargoDash Staff",
  CM: "CargoDash Management",
  "3PA": "3rd Party Agent",
  EXTERNAL: "External User",
  SA: "Accountant",
};

export default function Sidebar({ mobileOpen, setMobileOpen }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const items = NAV_ITEMS[user?.role] || [];
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [accountingUnread, setAccountingUnread] = useState(0);
  const [accountsUnread, setAccountsUnread] = useState(0);

  const fetchCounts = useCallback(async () => {
    try {
      const calls = [
        api.get("/notifications/count").catch(() => ({ data: { unread: 0 } })),
        api.get("/approvals/pending-count").catch(() => ({ data: { count: 0 } })),
      ];
      if (user?.role === "SA") calls.push(api.get("/accounting/unread-count").catch(() => ({ data: { count: 0 } })));
      if (user?.role === "CM") calls.push(api.get("/cm/accounts/unread-count").catch(() => ({ data: { count: 0 } })));
      const results = await Promise.all(calls);
      setUnreadCount(results[0].data.unread || 0);
      setPendingApprovals(results[1].data.count || 0);
      if (user?.role === "SA") setAccountingUnread(results[2]?.data?.count || 0);
      if (user?.role === "CM") setAccountsUnread(results[2]?.data?.count || 0);
    } catch { /* ignore */ }
  }, [user?.role]);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    const handleNotifChanged = () => fetchCounts();
    window.addEventListener("notif-changed", handleNotifChanged);
    window.addEventListener("accounting-changed", handleNotifChanged);
    window.addEventListener("cm-accounts-changed", handleNotifChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener("notif-changed", handleNotifChanged);
      window.removeEventListener("accounting-changed", handleNotifChanged);
      window.removeEventListener("cm-accounts-changed", handleNotifChanged);
    };
  }, [fetchCounts]);

  const sidebarContent = (
    <>
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div>
            <img src="/cargodash-logo.svg" alt="CargoDash" className="h-8" />
            <p className="text-[9px] uppercase tracking-[0.2em] text-slate-400 mt-1.5 pl-0.5">
              Skyworth JC System
            </p>
          </div>
          {setMobileOpen && (
            <button onClick={() => setMobileOpen(false)} className="lg:hidden text-slate-400 hover:text-slate-600">
              <X size={20} />
            </button>
          )}
        </div>
      </div>
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path ||
            (item.path !== "/" && location.pathname.startsWith(item.path));
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.slice(1)}`}
              onClick={() => setMobileOpen && setMobileOpen(false)}
              className={`sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm
                ${isActive
                  ? "bg-teal-50 text-teal-600 border-l-2 border-teal-500 font-medium"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-l-2 border-transparent"
                }`}
            >
              <Icon size={16} />
              <span className="flex-1">{item.label}</span>
              {item.path === "/notifications" && unreadCount > 0 && (
                <span className="bg-teal-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
              {item.path === "/approvals" && pendingApprovals > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {pendingApprovals > 99 ? "99+" : pendingApprovals}
                </span>
              )}
              {item.path === "/accounting" && accountingUnread > 0 && (
                <span
                  data-testid="sidebar-accounting-unread"
                  className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                >
                  {accountingUnread > 99 ? "99+" : accountingUnread}
                </span>
              )}
              {item.path === "/accounts" && accountsUnread > 0 && (
                <span
                  data-testid="sidebar-accounts-unread"
                  className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                >
                  {accountsUnread > 99 ? "99+" : accountsUnread}
                </span>
              )}
              {isActive && !["/notifications", "/approvals", "/accounting", "/accounts"].includes(item.path) && <ChevronRight size={14} className="ml-auto" />}
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-xl bg-teal-50 flex items-center justify-center">
            <Users size={14} className="text-teal-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate" data-testid="sidebar-user-name">
              {user?.name}
            </p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
              {ROLE_LABELS[user?.role] || user?.role}
            </p>
          </div>
        </div>
        <Button
          data-testid="logout-btn"
          variant="ghost"
          size="sm"
          onClick={logout}
          className="w-full justify-start text-slate-500 hover:text-red-500 hover:bg-red-50"
        >
          <LogOut size={14} className="mr-2" /> Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <>
      <aside
        data-testid="sidebar"
        className="hidden lg:flex w-60 h-screen bg-white border-r border-slate-200 flex-col fixed left-0 top-0 z-40"
      >
        {sidebarContent}
      </aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/20" onClick={() => setMobileOpen(false)} />
          <aside className="fixed left-0 top-0 w-64 h-screen bg-white border-r border-slate-200 flex flex-col z-50 shadow-xl">
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}
